import { zodResolver } from '@hookform/resolvers/zod';
import { Copy } from '@phosphor-icons/react';
import {
  getListApiKeysQueryKey,
  useCreateApiKey,
} from '@reelplicate/api-client/agent-access';
import type { CreatedApiKeyDto, WorkspaceRole } from '@reelplicate/api-client/model';
import { createApiKeyBodyNameMax } from '@reelplicate/api-client/zod/agent-access';
import {
  Button,
  Field,
  IconButton,
  Modal,
  ModalClose,
  ModalContent,
  Select,
  SelectItem,
  Stack,
  TextInput,
  toast,
  Well,
} from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { AGENT_ROLES } from './agents';

const KeySchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name the key after what uses it')
    .max(createApiKeyBodyNameMax, `Keep it under ${createApiKeyBodyNameMax} characters`),
  role: z.enum(['OWNER', 'EDITOR', 'REVIEWER']),
});
type KeyValues = z.infer<typeof KeySchema>;

/**
 * Name a key and pick its role, then show the key once to copy. Closing
 * forgets it: only a hash is kept. Mount it with a new `key` per opening so
 * it starts empty.
 */
export function CreateApiKeyModal({
  workspaceId,
  roles,
  open,
  onOpenChange,
}: {
  workspaceId: string;
  /** The roles the user can give a key. */
  roles: WorkspaceRole[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const formId = useId();
  const queryClient = useQueryClient();
  const create = useCreateApiKey();
  const [created, setCreated] = useState<CreatedApiKeyDto | null>(null);
  const form = useForm<KeyValues>({
    resolver: zodResolver(KeySchema),
    defaultValues: { name: '', role: roles.includes('EDITOR') ? 'EDITOR' : roles[0] },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      setCreated(await create.mutateAsync({ id: workspaceId, data: values }));
    } catch {
      return; // The failure is already shown as a toast.
    }
    await queryClient.invalidateQueries({
      queryKey: getListApiKeysQueryKey(workspaceId),
    });
  });

  const copy = () => {
    if (!created) return;
    navigator.clipboard?.writeText(created.key).then(
      () => toast.success('API key copied'),
      () => toast.error('Couldn’t copy. Select the key and copy it instead.'),
    );
  };

  if (created) {
    return (
      <Modal open={open} onOpenChange={onOpenChange}>
        <ModalContent
          size="sm"
          title={`${created.name} is ready`}
          description="Send it as a bearer token to the MCP server: Authorization: Bearer <key>."
          footer={
            <ModalClose asChild>
              <Button>Done</Button>
            </ModalClose>
          }
        >
          <Stack gap={4}>
            <Field label="API key">
              <TextInput
                readOnly
                value={created.key}
                onFocus={(e) => e.currentTarget.select()}
                trailingAction={
                  <IconButton
                    icon={Copy}
                    size="sm"
                    variant="ghost"
                    aria-label="Copy API key"
                    onClick={copy}
                  />
                }
              />
            </Field>
            <Well tone="warning" title="Copy it now">
              This is the only time the key is shown. If you lose it, revoke it and create
              another.
            </Well>
          </Stack>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent
        size="sm"
        title="Create API key"
        description="For scripts and automations like Zapier or n8n. The key acts as you, limited to its role."
        footer={
          <>
            <ModalClose asChild>
              <Button variant="ghost">Cancel</Button>
            </ModalClose>
            <Button type="submit" form={formId} loading={create.isPending}>
              Create key
            </Button>
          </>
        }
      >
        <Stack
          as="form"
          id={formId}
          gap={4}
          onSubmit={(e) => void onSubmit(e)}
          noValidate
        >
          <Controller
            name="name"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field label="Name" error={fieldState.error?.message}>
                <TextInput
                  {...field}
                  placeholder="n8n — weekly report"
                  autoComplete="off"
                />
              </Field>
            )}
          />
          <Controller
            name="role"
            control={form.control}
            render={({ field }) => (
              <Field label="Role" helper={AGENT_ROLES[field.value].description}>
                <Select value={field.value} onValueChange={field.onChange}>
                  {roles.map((role) => (
                    <SelectItem key={role} value={role}>
                      {AGENT_ROLES[role].name}
                    </SelectItem>
                  ))}
                </Select>
              </Field>
            )}
          />
        </Stack>
      </ModalContent>
    </Modal>
  );
}
